import React, { useEffect, useState } from "react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Checkbox } from "@/components/ui/checkbox";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { useSponsorshipStore } from "@/stores/sponsorship/sponsorship-store";
import { api } from "@/services/api/client";
import { useOrganizationStore } from "@/stores/organization/organization-store";
import { useAuthStore } from "@/stores/auth/auth-store";
import type {
  OrgRole,
  PendingProjectGrant,
  HomepageSetting,
  InviteBundleGrants,
} from "@/types/sponsorship";
import { getSponsorshipErrorMessage } from "@/utils/sponsorship";

interface OrgProject {
  _id: string;
  name: string;
}

interface OrgProjectPage {
  id: string;
  name?: string;
}

interface OrgScopedProjectsResponse {
  projects?: OrgProject[];
}

interface ProjectAccessResponse {
  canAccess?: boolean;
  pages?: OrgProjectPage[];
}

interface VaultResponseRecord {
  id?: string;
  _id?: string;
  name?: string;
}

interface VaultOption {
  id: string;
  name: string;
}

function toVaultOption(vault: VaultResponseRecord): VaultOption | null {
  const id =
    typeof vault.id === "string" && vault.id.trim().length > 0
      ? vault.id
      : typeof vault._id === "string" && vault._id.trim().length > 0
        ? vault._id
        : null;

  if (!id || typeof vault.name !== "string" || vault.name.trim().length === 0) {
    return null;
  }

  return {
    id,
    name: vault.name,
  };
}

function isOrgRole(value: string): value is OrgRole {
  return value === "admin" || value === "member" || value === "viewer";
}

function isProjectGrantRole(
  value: string,
): value is PendingProjectGrant["role"] {
  return value === "editor" || value === "viewer";
}

interface InviteSubAccountFormProps {
  masterBillingOrgId: string;
  onInviteSent?: (message: string) => void;
}

function normalizeInviteEmail(value: string): string {
  return value.trim().toLowerCase();
}

function maskEmailForInviteDebug(email: string): string {
  const normalizedEmail = normalizeInviteEmail(email);
  const [localPart = "", domain = ""] = normalizedEmail.split("@");
  if (!localPart || !domain) {
    return normalizedEmail ? "[invalid-email]" : "[empty]";
  }

  return `${localPart.slice(0, 2)}***@${domain}`;
}

function logSponsorshipInviteCreateDebug(
  event: string,
  metadata: Record<string, unknown>,
): void {
  console.info(`[sponsorship-invite:create] ${event}`, metadata);
}

export const InviteSubAccountForm: React.FC<InviteSubAccountFormProps> = ({
  masterBillingOrgId,
  onInviteSent,
}) => {
  const invite = useSponsorshipStore((state) => state.invite);
  const organizations = useOrganizationStore((state) => state.organizations);
  const currentUser = useAuthStore((state) => state.user);
  const [email, setEmail] = useState("");
  const [message, setMessage] = useState("");
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [successMessage, setSuccessMessage] = useState<string | null>(null);

  // Permission grants
  const [grantOrgAccess, setGrantOrgAccess] = useState(false);
  const [orgRole, setOrgRole] = useState<OrgRole>("member");
  const [orgProjects, setOrgProjects] = useState<OrgProject[]>([]);
  const [projectGrants, setProjectGrants] = useState<PendingProjectGrant[]>([]);
  const [homepage, setHomepage] = useState<HomepageSetting | null>(null);
  const [homepagePages, setHomepagePages] = useState<OrgProjectPage[]>([]);
  const [isHomepagePagesLoading, setIsHomepagePagesLoading] = useState(false);
  const [availableVaults, setAvailableVaults] = useState<VaultOption[]>([]);
  const [selectedVaultId, setSelectedVaultId] = useState("none");

  const selectedOrg = organizations.find(
    (org) => org._id === masterBillingOrgId,
  );
  const homepageEligibleProjects = projectGrants.map((grant) => ({
    _id: grant.projectId,
    name: grant.projectName,
  }));

  // Fetch projects for the selected org
  useEffect(() => {
    if (!masterBillingOrgId || !selectedOrg?.slug) {
      setOrgProjects([]);
      return;
    }
    let cancelled = false;
    void (async () => {
      try {
        const response = await api.get<OrgScopedProjectsResponse>(
          `/orgs/${selectedOrg.slug}/projects`,
          {
            headers: {
              "X-Organization-Id": masterBillingOrgId,
            },
          },
        );
        const projects = response.data?.projects;
        if (!cancelled && Array.isArray(projects)) {
          setOrgProjects(projects);
        }
      } catch {
        // If project fetch fails, just show empty list
        if (!cancelled) setOrgProjects([]);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [masterBillingOrgId, selectedOrg?.slug]);

  useEffect(() => {
    if (!masterBillingOrgId) {
      setAvailableVaults([]);
      return;
    }

    let cancelled = false;
    setAvailableVaults([]);

    void (async () => {
      try {
        const response = await api.get<VaultResponseRecord[]>("/billing/vaults", {
          headers: {
            "X-Organization-Id": masterBillingOrgId,
          },
        });
        const vaults = Array.isArray(response.data)
          ? response.data
              .map((vault) => toVaultOption(vault))
              .filter((vault): vault is VaultOption => vault !== null)
          : [];
        if (!cancelled) {
          setAvailableVaults(vaults);
        }
      } catch {
        if (!cancelled) {
          setAvailableVaults([]);
        }
      }
    })();

    return () => {
      cancelled = true;
    };
  }, [masterBillingOrgId]);

  useEffect(() => {
    setProjectGrants([]);
    setHomepage(null);
    setHomepagePages([]);
    setSelectedVaultId("none");
    setSuccessMessage(null);
  }, [masterBillingOrgId]);

  useEffect(() => {
    if (!homepage) {
      return;
    }

    const hasMatchingProjectGrant = projectGrants.some(
      (grant) => grant.projectId === homepage.projectId,
    );
    if (hasMatchingProjectGrant) {
      return;
    }

    setHomepage(null);
    setHomepagePages([]);
    setIsHomepagePagesLoading(false);
  }, [homepage, projectGrants]);

  useEffect(() => {
    const homepageProjectId = homepage?.projectId;
    if (!homepageProjectId) {
      setHomepagePages([]);
      setIsHomepagePagesLoading(false);
      return;
    }

    let cancelled = false;
    setHomepagePages([]);
    setIsHomepagePagesLoading(true);

    void (async () => {
      try {
        const response = await api.get<ProjectAccessResponse>(
          `/projects/${homepageProjectId}/can-access`,
        );
        const pages =
          response.data?.canAccess && Array.isArray(response.data.pages)
            ? response.data.pages.filter(
                (page): page is OrgProjectPage => typeof page?.id === "string",
              )
            : [];

        if (cancelled) {
          return;
        }

        setHomepagePages(pages);
        setHomepage((current) => {
          if (!current || current.projectId !== homepageProjectId) {
            return current;
          }

          if (!current.pageId) {
            return {
              ...current,
              pageName: null,
            };
          }

          const selectedPage = pages.find((page) => page.id === current.pageId);
          return {
            ...current,
            pageId: selectedPage?.id ?? null,
            pageName: selectedPage?.name ?? null,
          };
        });
      } catch {
        if (!cancelled) {
          setHomepagePages([]);
          setHomepage((current) => {
            if (!current || current.projectId !== homepageProjectId) {
              return current;
            }
            return {
              ...current,
              pageId: null,
              pageName: null,
            };
          });
        }
      } finally {
        if (!cancelled) {
          setIsHomepagePagesLoading(false);
        }
      }
    })();

    return () => {
      cancelled = true;
    };
  }, [homepage?.projectId]);

  const toggleProjectGrant = (project: OrgProject, checked: boolean) => {
    if (checked) {
      setProjectGrants((prev) => [
        ...prev,
        { projectId: project._id, projectName: project.name, role: "editor" },
      ]);
    } else {
      setProjectGrants((prev) =>
        prev.filter((grant) => grant.projectId !== project._id),
      );
    }
  };

  const updateProjectRole = (
    projectId: string,
    role: PendingProjectGrant["role"],
  ) => {
    setProjectGrants((prev) =>
      prev.map((grant) =>
        grant.projectId === projectId ? { ...grant, role } : grant,
      ),
    );
  };

  const handleSubmit = async (event: React.FormEvent) => {
    event.preventDefault();
    setIsSubmitting(true);
    setError(null);
    setSuccessMessage(null);

    const submittedEmail = email.trim();
    const normalizedSubmittedEmail = normalizeInviteEmail(submittedEmail);
    const normalizedCurrentUserEmail = normalizeInviteEmail(
      currentUser?.email ?? "",
    );
    const isSelfInvite = Boolean(
      normalizedSubmittedEmail &&
        normalizedCurrentUserEmail &&
        normalizedSubmittedEmail === normalizedCurrentUserEmail,
    );

    logSponsorshipInviteCreateDebug("submit", {
      inviteeEmail: maskEmailForInviteDebug(submittedEmail),
      currentUserEmail: maskEmailForInviteDebug(currentUser?.email ?? ""),
      masterBillingOrgId,
      isSelfInvite,
    });

    if (isSelfInvite) {
      setError(
        "Enter the email address of the person you want to invite. You cannot invite your own account.",
      );
      setIsSubmitting(false);
      return;
    }

    try {
      const grants: InviteBundleGrants = {};
      if (grantOrgAccess) {
        grants.org = { orgRole };
      }
      if (projectGrants.length > 0) {
        grants.projects = projectGrants;
      }
      if (selectedVaultId !== "none") {
        const selectedVault = availableVaults.find(
          (vault) => vault.id === selectedVaultId,
        );
        if (selectedVault) {
          grants.vaultAssignment = {
            vaultId: selectedVault.id,
            vaultName: selectedVault.name,
          };
        }
      }
      if (homepage) {
        grants.homepage = homepage;
      }

      const hasGrants =
        grants.org !== undefined ||
        grants.projects !== undefined ||
        grants.vaultAssignment !== undefined ||
        grants.homepage !== undefined;
      await invite({
        masterBillingOrgId,
        email: submittedEmail,
        message: message.trim() || undefined,
        grants: hasGrants ? grants : undefined,
      });
      const deliveryMessage = `Invite sent to ${submittedEmail}. It will count against this sponsorship cap until it is accepted, canceled, or expires.`;
      setEmail("");
      setMessage("");
      setGrantOrgAccess(false);
      setOrgRole("member");
      setProjectGrants([]);
      setHomepage(null);
      setSelectedVaultId("none");
      if (onInviteSent) {
        onInviteSent(deliveryMessage);
      } else {
        setSuccessMessage(deliveryMessage);
      }
    } catch (submitError) {
      logSponsorshipInviteCreateDebug("failed", {
        inviteeEmail: maskEmailForInviteDebug(submittedEmail),
        hasMessage: Boolean(
          getSponsorshipErrorMessage(submitError, "Unable to send invite"),
        ),
      });
      setError(getSponsorshipErrorMessage(submitError, "Unable to send invite"));
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <form onSubmit={handleSubmit} className="space-y-4">
      <div className="grid gap-2">
        <Label htmlFor="sub-account-email">Email</Label>
        <Input
          id="sub-account-email"
          name="subAccountInviteEmail"
          type="email"
          autoComplete="off"
          value={email}
          onChange={(event) => setEmail(event.target.value)}
          required
        />
      </div>
      <div className="grid gap-2">
        <Label htmlFor="sub-account-message">Message</Label>
        <Textarea
          id="sub-account-message"
          value={message}
          maxLength={500}
          onChange={(event) => setMessage(event.target.value)}
        />
      </div>

      {/* Optional permission grants */}
      <div className="space-y-3 rounded-md border p-4">
        <p className="text-sm font-medium">
          Optional: Configure access on accept
        </p>
        <p className="text-xs text-muted-foreground">
          Sponsorship only covers AI budget. Workspace membership, project access,
          and AI budget routing must be granted explicitly below.
        </p>

        {/* Org membership grant */}
        <div className="flex items-center gap-2">
          <Checkbox
            id="grant-org-access"
            checked={grantOrgAccess}
            onCheckedChange={(checked) => setGrantOrgAccess(checked === true)}
          />
          <Label htmlFor="grant-org-access" className="text-sm font-normal">
            Add to {selectedOrg?.name ?? "organization"} as
          </Label>
          <Select
            value={orgRole}
            onValueChange={(value) => {
              if (isOrgRole(value)) {
                setOrgRole(value);
              }
            }}
            disabled={!grantOrgAccess}
          >
            <SelectTrigger className="w-32">
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="admin">Admin</SelectItem>
              <SelectItem value="member">Member</SelectItem>
              <SelectItem value="viewer">Viewer</SelectItem>
            </SelectContent>
          </Select>
        </div>

        {/* Project access grants */}
        <div className="space-y-2">
          <p className="text-sm text-muted-foreground">Project access</p>
          {orgProjects.length > 0 ? (
            orgProjects.map((project) => {
              const grant = projectGrants.find(
                (g) => g.projectId === project._id,
              );
              return (
                <div key={project._id} className="flex items-center gap-2">
                  <Checkbox
                    id={`project-${project._id}`}
                    checked={grant !== undefined}
                    onCheckedChange={(checked) =>
                      toggleProjectGrant(project, checked === true)
                    }
                  />
                  <Label
                    htmlFor={`project-${project._id}`}
                    className="flex-1 text-sm font-normal"
                  >
                    {project.name}
                  </Label>
                  {grant ? (
                    <Select
                      value={grant.role}
                      onValueChange={(value) => {
                        if (isProjectGrantRole(value)) {
                          updateProjectRole(project._id, value);
                        }
                      }}
                    >
                      <SelectTrigger className="w-28">
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="editor">Editor</SelectItem>
                        <SelectItem value="viewer">Viewer</SelectItem>
                      </SelectContent>
                    </Select>
                  ) : null}
                </div>
              );
            })
          ) : (
            <p className="text-xs text-muted-foreground">
              No projects in this organization yet.
            </p>
          )}
        </div>

        <div className="grid gap-2">
          <Label htmlFor="vault-assignment" className="text-sm">
            AI budget vault on accept
          </Label>
          <p className="text-xs text-muted-foreground">
            Optional. Choose the vault that should cover this user&apos;s sponsored AI usage.
          </p>
          <Select
            value={selectedVaultId}
            onValueChange={setSelectedVaultId}
          >
            <SelectTrigger id="vault-assignment">
              <SelectValue placeholder="Use existing vault matching" />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="none">Use existing vault matching</SelectItem>
              {availableVaults.map((vault) => (
                <SelectItem key={vault.id} value={vault.id}>
                  {vault.name}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
          {availableVaults.length === 0 ? (
            <p className="text-xs text-muted-foreground">
              No vaults are available for this organization yet.
            </p>
          ) : null}
        </div>

        {/* Homepage setting */}
        <div className="grid gap-2">
          <Label htmlFor="homepage-project" className="text-sm">
            Homepage after accept
          </Label>
          <p className="text-xs text-muted-foreground">
            Choose from the projects granted above. Homepage selection does not
            grant project access by itself.
          </p>
          <Select
            value={homepage?.projectId ?? "none"}
            disabled={homepageEligibleProjects.length === 0}
            onValueChange={(value) => {
              if (value === "none") {
                setHomepage(null);
                return;
              }
              const project = homepageEligibleProjects.find(
                (candidate) => candidate._id === value,
              );
              if (project) {
                setHomepage({
                  projectId: project._id,
                  projectName: project.name,
                  pageId: null,
                  pageName: null,
                });
              }
            }}
          >
            <SelectTrigger id="homepage-project">
              <SelectValue placeholder="Default (dashboard)" />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="none">Default (dashboard)</SelectItem>
              {homepageEligibleProjects.map((project) => (
                <SelectItem key={project._id} value={project._id}>
                  {project.name}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
          {homepageEligibleProjects.length === 0 ? (
            <p className="text-xs text-muted-foreground">
              Select a project under Project access before assigning a homepage.
            </p>
          ) : null}
          {homepage ? (
            <div className="grid gap-2">
              <Label htmlFor="homepage-page" className="text-sm">
                Homepage page
              </Label>
              <p className="text-xs text-muted-foreground">
                Optional. Leave blank to send them to the project overview.
              </p>
              <Select
                value={homepage.pageId ?? "none"}
                onValueChange={(value) => {
                  if (value === "none") {
                    setHomepage((current) =>
                      current
                        ? {
                            ...current,
                            pageId: null,
                            pageName: null,
                          }
                        : current,
                    );
                    return;
                  }

                  const page = homepagePages.find((candidate) => candidate.id === value);
                  if (!page) {
                    return;
                  }

                  setHomepage((current) =>
                    current
                      ? {
                          ...current,
                          pageId: page.id,
                          pageName: page.name ?? null,
                        }
                      : current,
                  );
                }}
                disabled={isHomepagePagesLoading || homepagePages.length === 0}
              >
                <SelectTrigger id="homepage-page">
                  <SelectValue
                    placeholder={
                      isHomepagePagesLoading
                        ? "Loading pages..."
                        : "Project overview"
                    }
                  />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="none">Project overview</SelectItem>
                  {homepagePages.map((page) => (
                    <SelectItem key={page.id} value={page.id}>
                      {page.name ?? page.id}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
              {!isHomepagePagesLoading && homepagePages.length === 0 ? (
                <p className="text-xs text-muted-foreground">
                  No homepage pages are available for this project.
                </p>
              ) : null}
            </div>
          ) : null}
        </div>
      </div>

      {successMessage ? (
        <div className="rounded-md border border-emerald-200 bg-emerald-50 p-3 text-sm text-emerald-800">
          {successMessage}
        </div>
      ) : null}
      {error ? <p className="text-sm text-destructive">{error}</p> : null}
      <Button type="submit" disabled={isSubmitting || !email.trim()}>
        {isSubmitting ? "Sending..." : "Invite"}
      </Button>
    </form>
  );
};
